/* Matchabear cinematic hero.
 *
 * The story is driven entirely by CSS scroll-driven animations (view-timeline),
 * which run on the compositor thread. This file deliberately does NOT listen to
 * scroll, wheel or keydown. The previous version called preventDefault on wheel
 * and drove window.scrollTo in a requestAnimationFrame loop to snap between
 * steps: that is scrolljacking, and it is why the section felt stuck, ignored
 * the scrollbar, and swallowed arrow keys and space. Native scrolling is now
 * left completely alone.
 *
 * The clips ARE scrubbed: scroll position drives video.currentTime, which is what
 * makes the pour follow your finger instead of running on its own clock. The old
 * version was janky because it seeked on every scroll event against clips with a
 * single keyframe. Both halves are fixed now:
 *   - mb-cinematic-*-scrub-v2.mp4 are all-intra (240 keyframes / 240 frames), so
 *     every frame is a seek target and the decoder never rebuilds delta frames.
 *   - seeking happens once per animation frame, from a rAF loop that only runs
 *     while the section is on screen, and only when the target moved by more than
 *     one frame.
 * Reference: scrub smoothness is dominated by keyframe interval (ffmpeg -g 1 /
 * x264 keyint=1), not by the JS. See muffinman.io/blog/scrubbing-videos-using-javascript.
 *
 * Safari has no scroll-driven animations (as of Safari 17, which is where macOS
 * Ventura and a large slice of iOS sit), and this store is ~71% mobile. So where
 * `animation-timeline` is missing we do NOT fall back to a static hero: we drive
 * the exact same beat ranges from a rAF-throttled scroll read. Still no
 * preventDefault, no scrollTo, no scrolljacking. The static hero is now only for
 * reduced-motion and for no-JS.
 *
 * Phones do not get the video at all. Scrubbing means a decode per scroll frame,
 * and iOS Low Power Mode blocks the muted play() the scrub needs to prime the
 * decoder. Instead they scrub a portrait WebP sequence onto a canvas: EVERY frame
 * of the three trimmed portrait clips (444), each pinned to the pin progress where
 * it is exact. A decoded video was tried for the same density and a fast thumb
 * outran the decoder; a still decodes on its own and cannot fall behind. See
 * initSequence() and scripts/build-mobile-hero-sequence.py.
 *
 * What is left: decode video only while visible, fade the poster once a real
 * frame exists, flag the header while the hero covers it.
 */
(() => {
  const SELECTOR = '[data-mb-cinematic]';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const supportsTimeline =
    typeof CSS !== 'undefined' &&
    typeof CSS.supports === 'function' &&
    CSS.supports('animation-timeline: view()');

  const instances = new WeakMap();
  /* ?mb-debug in the URL logs every canvas draw to window.__mbCine as
     [ms, scroll progress, progress of the frame drawn, 's'], which is how scroll
     probes see stepping and jumps against the scroll. */
  const DEBUG = /[?&]mb-debug\b/.test(window.location.search);
  const trace = (p, shownP, src) => { if (DEBUG) (window.__mbCine = window.__mbCine || []).push([performance.now(), p, shownP, src]); };
  const clamp = (n, lo, hi) => (n < lo ? lo : n > hi ? hi : n);

  /* Piecewise-linear read of a keyframe list: [[stop, value], ...] with stop 0..1. */
  const track = (t, stops) => {
    if (t <= stops[0][0]) return stops[0][1];
    for (let i = 1; i < stops.length; i += 1) {
      const [s1, v1] = stops[i];
      if (t <= s1) {
        const [s0, v0] = stops[i - 1];
        const span = s1 - s0;
        return span === 0 ? v1 : v0 + ((t - s0) / span) * (v1 - v0);
      }
    }
    return stops[stops.length - 1][1];
  };

  /* Mirrors the @keyframes + animation-range pairs in mb-cinematic-matcha.css.
     Keep the two in step: the CSS path is what most browsers actually run. */
  /* Five beats, but NOT five equal fifths. The intro line and "Add water" split
     the first quarter; the other three keep a quarter each. The phone stills cut
     the pin by frame count (four stills, so quarters), and fifths would have
     drifted every copy swap off its image swap. Mirrors the CSS ranges. */
  const BEATS = [
    { range: [0.000, 0.0625], opacity: [[0, 1], [0.86, 1], [1, 0]], shift: [[0, 0], [0.86, 0], [1, -22]], scale: [[0, 1], [0.86, 1], [1, 1.015]] },
    /* "Sift the matcha" (2026-09-27): the intro line now shares its eighth with it. DOM order = this order. */
    { range: [0.0625, 0.125], opacity: [[0, 0], [0.14, 1], [0.86, 1], [1, 0]], shift: [[0, 22], [0.14, 0], [0.86, 0], [1, -22]], scale: [[0, 0.985], [0.14, 1], [0.86, 1], [1, 1.015]] },
    { range: [0.125, 0.250], opacity: [[0, 0], [0.14, 1], [0.86, 1], [1, 0]], shift: [[0, 22], [0.14, 0], [0.86, 0], [1, -22]], scale: [[0, 0.985], [0.14, 1], [0.86, 1], [1, 1.015]] },
    { range: [0.250, 0.500], opacity: [[0, 0], [0.14, 1], [0.86, 1], [1, 0]], shift: [[0, 22], [0.14, 0], [0.86, 0], [1, -22]], scale: [[0, 0.985], [0.14, 1], [0.86, 1], [1, 1.015]] },
    { range: [0.500, 0.750], opacity: [[0, 0], [0.14, 1], [0.86, 1], [1, 0]], shift: [[0, 22], [0.14, 0], [0.86, 0], [1, -22]], scale: [[0, 0.985], [0.14, 1], [0.86, 1], [1, 1.015]] },
    { range: [0.750, 1.000], opacity: [[0, 0], [0.14, 1], [1, 1]], shift: [[0, 22], [0.14, 0], [1, 0]], scale: [[0, 0.985], [0.14, 1], [1, 1]] },
  ];
  /* The cross-fade from bowl to glass sits on the beat boundary, where no line is
     on screen. It used to run 0.42-0.58, 460px of scroll with the whisked bowl and
     the glass stacked at half strength each. */
  const RITUAL_OPACITY = [[0, 1], [0.46, 1], [0.54, 0], [1, 0]];
  const LATTE_OPACITY = [[0, 0], [0.46, 0], [0.54, 1], [1, 1]];

  /* Where each clip's playhead sits (0..1 of its own footage) at each point of the
     pin, so every line lands on the action it names. Both masters are 240 frames,
     numbered as a filmstrip of them reads (2026-09-18):
       ritual: f8 is the last dry bowl; powder falls f9-56, the kettle pours f60-92, the whisk comes in
               at f100, whisks until f212 and rests in the bowl by f236
       latte:  the bowl tips over the glass from f84, the ribbon meets the milk at
               f104 and marbles until ~f130; at f224 a second stream starts, so the
               track stops short of it
     The old version was linear: ritual from 0.16 across p 0-0.58. The arithmetic
     aimed the pour at "Add water", but it put the pour under the intro line and
     the whisk under "Add water". */
  const frame = (n) => n / 239; // MbCineScrubber.seek(t) draws frame round(t * 239)
  const CLIP_TRACK = {
    // Hold the dry bowl for the intro; show the whole fall under "Sift the matcha".
    ritual: [[0, frame(8)], [0.0625, frame(8)], [0.12, frame(58)], [0.13, frame(62)], [0.235, frame(92)],
      [0.255, frame(100)], [0.29, frame(110)], [0.465, frame(212)], [0.54, frame(236)]],
    latte: [[0.46, frame(84)], [0.54, frame(104)], [0.7, frame(130)], [1, frame(218)]],
  };
  const FRAME = 1 / 30; // the clips are 30fps; seeking below this is wasted work

  /* Phones get the still story, not the scrubbed clips. See the comment on the
     stills in mb-cinematic-matcha.liquid: the two masters are 16.6MB together,
     every scroll frame is a decode, and iOS Low Power Mode blocks the priming
     play() the scrub depends on. Same breakpoint as the CSS. */
  const VIDEO_VIEWPORT = '(min-width: 750px)';
  /* Phones hang the beats from a line under the header (mb-mobile.css, 23.09) instead of
     centring them, so the JS driver must not add the desktop -50% there. Without it,
     every browser that lacks animation-timeline (Safari before 26) lifted the line into
     the logo (Rauno's iPhone, 27.09). Same breakpoint as the CSS. */
  const HANG_VIEWPORT = window.matchMedia('(max-width: 749px)');

  /* Phones scrub a pre-decoded image sequence instead of the video. Frame 0 is
     also the <picture> poster, so it is already in cache by the time we draw. */
  const DPR_CAP = 2;

  /* Damping. Scroll position is a step function: a trackpad flick delivers a
     handful of large jumps, so mapping it straight onto a frame index gives an
     animation exactly as jerky as the input device. Instead the rendered
     progress CHASES the scroll progress. TAU is the time constant in seconds —
     the smaller it is, the tighter the follow. 0.085 lags by a few frames, which
     reads as weight rather than as delay.

     The step is exp-based, not a fixed lerp factor, so the follow is identical at
     60Hz and 120Hz instead of twice as fast on a ProMotion display. */
  const TAU = 0.085;
  const SETTLED = 0.0004; // stop the loop once the gap is smaller than a frame step

  class CinematicHero {
    constructor(root) {
      this.root = root;
      this.videos = Array.from(root.querySelectorAll('video'));
      this.videoEnabled = window.matchMedia(VIDEO_VIEWPORT).matches;
      this.setupScrollDriver();

      this.onReduced = this.onReduced.bind(this);

      if (this.videoEnabled) {
        this.videos.forEach((video) => {
          if (video.readyState >= 2) this.markReady();
          else video.addEventListener('loadeddata', () => this.markReady(), { once: true });
          // duration is needed before a scroll position can be turned into a timecode
          video.addEventListener('loadedmetadata', () => this.paint(), { once: true });
        });
      }

      this.observer = new IntersectionObserver(
        (entries) => entries.forEach((entry) => this.onVisibility(entry)),
        { threshold: [0, 0.6] }
      );
      this.observer.observe(root);

      reduced.addEventListener('change', this.onReduced);
    }

    markReady() {
      this.root.classList.add('is-ready');
    }

    /* The markup ships the clips with no <source> and no poster so that nothing
       is fetched until we say so. On a phone we never say so. */
    attachVideoSources() {
      this.videos.forEach((video) => {
        const src = video.dataset.mbVideoSrc;
        if (!src || video.querySelector('source')) return;
        const poster = video.dataset.mbVideoPoster;
        if (poster) video.setAttribute('poster', poster);
        const source = document.createElement('source');
        source.type = 'video/mp4';
        source.src = src;
        video.appendChild(source);
        video.preload = 'auto';
        video.load();
      });
    }

    /* ---- phone frame sequence -------------------------------------------
       444 WebP frames at 480x854 (every source frame); frame 0 is the 720x1280
       poster itself, so the first draw is the image already on screen. data-mb-seq carries the timeline:
       p[i] is the pin progress at which frame i is exact, x the two scene-cut
       dissolves. Both come from snippets/mb-cine-seq.liquid, which
       scripts/build-mobile-hero-sequence.py writes together with the frames.

       We hold plain Image objects, not ImageBitmaps. An ImageBitmap pins its
       decoded pixels (~2MB each at this size), which is a real risk on a phone.
       Images let the browser drop and re-decode as it sees fit, which is the
       whole reason this technique scales.
    */
    initSequence() {
      if (!this.seq) return;
      const list = (this.root.dataset.mbFrames || '').split(',')
        .map((u) => u.trim()).filter(Boolean);
      let timeline = null;
      try { timeline = JSON.parse(this.root.dataset.mbSeq || 'null'); } catch (e) { /* no timeline, no sequence */ }
      if (!list.length || !timeline || !Array.isArray(timeline.p) || timeline.p.length !== list.length) {
        this.seq = null;
        return;
      }
      this.seqP = timeline.p;
      this.seqX = timeline.x || [];

      this.ctx = this.seq.getContext('2d', { alpha: false });
      this.frames = list.map(() => null);
      this.warm = [];
      this.sizeCanvas();

      const load = (i, low) => new Promise((res) => {
        const img = new Image();
        // Shopify asset URLs can redirect to a CDN origin. Anonymous CORS keeps
        // the canvas readable and prevents a silent SecurityError on pixel work.
        img.crossOrigin = 'anonymous';
        img.decoding = 'async';
        if (low) img.fetchPriority = 'low';
        img.onload = () => { this.frames[i] = img; res(); };
        img.onerror = res;
        img.src = list[i];
      });

      /* Coarse to fine. The first pass (the scene cuts, then every 8th frame) makes
         the whole story scrub after a few hundred KB; each later pass halves the
         gaps, at low priority so it does not hold up the rest of the page.
         nearestLoaded() draws the closest frame that has arrived, so the motion
         only gets finer as frames land and never shows a hole. With Save-Data on,
         or on a 2G/3G connection, loading stops at every 4th frame. */
      const n = list.length;
      const conn = navigator.connection || {};
      const lean = !!conn.saveData || /(^|-)[23]g$/.test(conn.effectiveType || '');
      const seen = new Set([0]);
      const order = [];
      const push = (i) => { if (i > 0 && i < n && !seen.has(i)) { seen.add(i); order.push(i); } };
      this.seqX.forEach((x) => { push(x[2]); push(x[3]); });
      let coarse = 0;
      [8, 4, 2, 1].forEach((step) => {
        if (lean && step < 4) return;
        for (let i = 0; i < n; i += step) push(i);
        push(n - 1);
        if (step === 8) coarse = order.length;
      });

      /* Only frame 0 and the coarse pass load before the page's load event. An
         image requested from script holds the load event open, so fetching all 444
         up front kept the browser "loading" (Safari's progress bar, anything that
         waits for window load) until the last frame: 20.8s on a throttled 4G run.
         The rest start once the page has loaded. */
      let fine = document.readyState === 'complete';
      // frame 0 first so there is something to paint at once, then six requests
      // in flight: HTTP/2 to one CDN host, and 444 small files to get through
      load(0).then(() => {
        this.frameIdx = -1;
        this.paint();
        let k = 0;
        const next = () => {
          if (k >= order.length || (!fine && k >= coarse)) return;
          const j = order[k];
          const low = k >= coarse;
          k += 1;
          load(j, low).then(() => {
            // standing in for a frame that has not arrived: a nearer one may have
            if (this.frameIdx !== this.pendingIdx) this.paint(this.rp);
            next();
          });
        };
        for (let c = 0; c < 6; c += 1) next();
        if (!fine) {
          const go = () => { fine = true; for (let c = 0; c < 6; c += 1) next(); };
          // the page may have finished loading while frame 0 was on its way
          if (document.readyState === 'complete') go();
          else window.addEventListener('load', go, { once: true });
        }
      });

      this.onResize = () => { this.sizeCanvas(); this.paint(); };
      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('orientationchange', this.onResize, { passive: true });
    }

    sizeCanvas() {
      if (!this.seq) return;
      const r = this.seq.getBoundingClientRect();
      if (!r.width || !r.height) return;
      const dpr = Math.min(window.devicePixelRatio || 1, DPR_CAP);
      const w = Math.round(r.width * dpr);
      const h = Math.round(r.height * dpr);
      if (this.seq.width !== w || this.seq.height !== h) {
        this.seq.width = w;
        this.seq.height = h;
        this.frameIdx = -1; // backing store was cleared
      }
    }

    /* The nearest frame that has actually arrived, so a sequence still streaming
       in shows the closest thing rather than a hole. */
    nearestLoaded(idx) {
      if (this.frames[idx]) return idx;
      for (let d = 1; d < this.frames.length; d += 1) {
        if (this.frames[idx - d]) return idx - d;
        if (this.frames[idx + d]) return idx + d;
      }
      return -1;
    }

    cover(img) {
      const cw = this.seq.width;
      const ch = this.seq.height;
      const s = Math.max(cw / img.naturalWidth, ch / img.naturalHeight);
      const dw = img.naturalWidth * s;
      const dh = img.naturalHeight * s;
      this.ctx.drawImage(img, (cw - dw) / 2, (ch - dh) / 2, dw, dh);
    }

    /* The frame whose timeline position is nearest p. */
    nearestIndex(p) {
      const t = this.seqP;
      let lo = 0;
      let hi = t.length - 1;
      if (p <= t[lo]) return lo;
      if (p >= t[hi]) return hi;
      while (hi - lo > 1) {
        const mid = (lo + hi) >> 1;
        if (t[mid] <= p) lo = mid; else hi = mid;
      }
      return p - t[lo] < t[hi] - p ? lo : hi;
    }

    /* Decode the next few frames in the direction of travel before they are
       drawn, so drawImage does not stall the main thread on a decode mid-scroll.
       Only the last dozen are remembered: the browser may drop older ones. */
    predecode(i, dir) {
      for (let k = 1; k <= 3; k += 1) {
        const j = i + dir * k;
        const img = this.frames[j];
        if (!img || !img.decode || this.warm.includes(j)) continue;
        this.warm.push(j);
        if (this.warm.length > 12) this.warm.shift();
        img.decode().catch(() => {});
      }
    }

    /* PHONE ONLY. Desktop scrubs every frame of real video, and so does the phone
       now: every source frame as a still (the 72-frame set was a slideshow, the
       181-frame set still stepped 2-3 source frames per change). Exact frames only,
       since dissolving two moving frames double-edged the whisk; the two blends are
       the scene cuts between clips, where a short dissolve IS the cut.

       Desktop never reaches this — it scrubs the real video and is untouched. */
    drawFrame(p) {
      if (!this.seq || !this.ctx || !this.seqP) return;
      // cheap: one rect read per rAF, and sizeCanvas only writes when it changed.
      // (The canvas default backing store is 300x150 — truthy — so guarding on
      // `!this.seq.width` never fires.)
      this.sizeCanvas();
      const cw = this.seq.width;
      const ch = this.seq.height;
      if (!cw || !ch) return;

      const progress = clamp(p, 0, 1);
      const cut = this.seqX.find((x) => progress > x[0] && progress < x[1]);
      let want = this.nearestIndex(progress);
      let other = -1;
      let mix = 0;
      if (cut) {
        want = cut[2];
        other = cut[3];
        // 24 steps are a smooth dissolve and let an unchanged step skip the redraw
        mix = Math.round(((progress - cut[0]) / (cut[1] - cut[0])) * 24) / 24;
      }
      this.pendingIdx = want;

      const a = this.nearestLoaded(want);
      if (a < 0) return;
      const b = other >= 0 ? this.nearestLoaded(other) : -1;

      trace(progress, this.seqP[a], 's');
      // redraw only when the picture changed, or the canvas was resized
      if (this.frameIdx === a && this.blendIdx === b && this.blendFrac === mix) return;

      const imgA = this.frames[a];
      if (!imgA || !imgA.naturalWidth) return;
      this.ctx.globalAlpha = 1;
      this.cover(imgA);
      if (b >= 0 && b !== a && mix > 0) {
        const imgB = this.frames[b];
        if (imgB && imgB.naturalWidth) {
          this.ctx.globalAlpha = mix;
          this.cover(imgB);
          this.ctx.globalAlpha = 1;
        }
      }
      this.predecode(want, want >= (this.frameIdx < 0 ? want : this.frameIdx) ? 1 : -1);
      this.frameIdx = a;
      this.blendIdx = b;
      this.blendFrac = mix;
      if (!this.root.classList.contains('is-seq')) this.root.classList.add('is-seq');
    }

    /* Reads scroll, writes styles and video.currentTime. Never fights the user:
       no preventDefault, no scrollTo, listener is passive. */
    setupScrollDriver() {
      if (reduced.matches) {
        this.root.classList.add('mb-cinematic--static');
        return;
      }
      // Beats come from CSS where scroll-driven animations exist; JS only fills that gap.
      this.drivesBeats = !supportsTimeline;
      if (this.drivesBeats) this.root.classList.add('mb-cinematic--js');

      this.beats = Array.from(this.root.querySelectorAll('.mb-cinematic__beat'));
      this.media = this.root.querySelector('.mb-cinematic__media');
      this.ritual = this.root.querySelector('.mb-cinematic__video--ritual');
      this.latte = this.root.querySelector('.mb-cinematic__video--latte');
      this.seq = this.root.querySelector('[data-mb-cinematic-seq]');
      this.frames = [];
      this.frameIdx = -1;
      if (!this.videoEnabled) this.initSequence();
      if (this.videoEnabled && !this.initCodecs()) this.attachVideoSources();

      /* rendered progress, chasing this.progress() rather than equalling it */
      this.rp = this.progress();
      this.running = false;
      this.lastT = 0;

      this.follow = (now) => {
        const dt = this.lastT ? Math.min((now - this.lastT) / 1000, 0.1) : 1 / 60;
        this.lastT = now;
        const target = this.progress();
        const gap = target - this.rp;
        if (Math.abs(gap) < SETTLED) {
          this.rp = target;
          this.paint(this.rp);
          this.running = false;   // settled: stop burning frames until the next scroll
          return;
        }
        this.rp += gap * (1 - Math.exp(-dt / TAU));
        this.paint(this.rp);
        this.rafId = requestAnimationFrame(this.follow);
      };

      this.kick = () => {
        if (this.running) return;
        this.running = true;
        this.lastT = 0;
        this.rafId = requestAnimationFrame(this.follow);
      };

      this.onScroll = this.kick;
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.paint(this.rp);
    }

    /* WebCodecs path, DESKTOP ONLY. Decodes the exact frame instead of asking the
       element to seek, which is the only way this scrubs smoothly in reverse — and
       the only way it can be tested locally at all, since `shopify theme dev`
       serves no Range support and leaves video.seekable at [0,0].

       Both clips composite onto the one canvas the phone path already uses, with
       the same cross-fade curve the <video> opacities used, so no markup changes
       and no second canvas. If anything here fails we leave the old elements
       exactly as they were and the currentTime path carries on. */
    initCodecs() {
      if (this.codecFailed) return false;
      if (this.codecLoading) return true; // Reuse decoders when Reduce Motion is toggled.
      if (!window.MbCineScrubber || !window.MbCineScrubber.supported()) return false;
      if (!this.seq || !this.ritual || !this.latte || !this.root.dataset.mbMp4box) return false;
      const a = this.ritual.dataset.mbVideoSrc, b = this.latte.dataset.mbVideoSrc;
      if (!a || !b) return false;
      this.codecLoading = true;
      const failed = (e) => {
        if (!this.codecLoading || this.dead) return;
        console.warn('[mb-cine] scrubber unavailable; using native video', e);
        this.codecLoading = false;
        this.codecFailed = true;
        this.scrubbers.forEach((s) => s.destroy());
        this.codecs = null;
        this.root.classList.remove('mb-cinematic--codecs', 'is-ready');
        this.ritual.style.display = '';
        this.latte.style.display = '';
        this.attachVideoSources();
        this.paint(this.rp);
      };
      const mk = (url, which) => new window.MbCineScrubber(url, {
        mp4boxUrl: this.root.dataset.mbMp4box,
        onReady: (inst) => {
          if (!this.codecLoading || this.dead) return;
          this.codecs = this.codecs || {};
          this.codecs[which] = inst;
          if (!this.ctx) {
            this.ctx = this.seq.getContext('2d', { alpha: false, desynchronized: true });
            if (!this.ctx) throw new Error('no hero canvas context');
            this.sizeCanvas();
          }
          this.paint(this.rp);
        },
        onFrame: () => { if (!this.dead && this.codecLoading) this.paint(this.rp); },
        onError: failed
      });
      const ritual = mk(a, 'ritual'), latte = mk(b, 'latte');
      this.scrubbers = [ritual, latte];
      // One download at a time: opening frames stream first; latte follows.
      // Native sources stay detached unless WebCodecs fails or is unavailable.
      ritual.init().then(() => {
        if (this.codecLoading && !this.dead) return latte.init();
      }).catch(failed);
      return true;
    }

    paintCodecs(p) {
      const c = this.codecs;
      if (!c || !this.ctx) return false;
      if (c.ritual) c.ritual.seek(track(p, CLIP_TRACK.ritual));
      if (c.latte) c.latte.seek(track(p, CLIP_TRACK.latte));
      const ar = track(p, RITUAL_OPACITY);
      const al = track(p, LATTE_OPACITY);
      // Keep the poster/last frame until every visible layer has a decoded frame.
      if ((ar > 0.001 && (!c.ritual || c.ritual.have < 0)) ||
          (al > 0.001 && (!c.latte || c.latte.have < 0))) return false;
      this.root.classList.add('mb-cinematic--codecs');
      this.sizeCanvas();
      const cw = this.seq.width, ch = this.seq.height;
      if (!cw || !ch) return false;
      this.ctx.fillStyle = '#080908';
      this.ctx.fillRect(0, 0, cw, ch);
      if (c.ritual) c.ritual.paintTo(this.ctx, cw, ch, ar);
      if (c.latte) c.latte.paintTo(this.ctx, cw, ch, al);
      this.root.classList.add('mb-cinematic--codecs', 'is-ready');
      this.ritual.style.display = 'none';
      this.latte.style.display = 'none';
      return true;
    }

    /* A video that has never played will not always render a seeked frame, especially
       on iOS. One muted play/pause primes the decoder, then we only ever seek. */
    primeForScrub(video) {
      if (!video.querySelector('source') || video.dataset.mbPrimed) return;
      video.dataset.mbPrimed = '1';
      const attempt = video.play();
      const settle = () => video.pause();
      if (attempt && attempt.then) attempt.then(settle).catch(settle);
      else settle();
    }

    scrub(video, key, p) {
      if (!video || !video.duration || Number.isNaN(video.duration)) return;
      this.primeForScrub(video);
      if (!video.paused) video.pause();
      const target = track(p, CLIP_TRACK[key]) * video.duration;
      // seeking costs a decode, so skip anything the eye cannot resolve
      if (Math.abs(video.currentTime - target) < FRAME) return;
      try { video.currentTime = target; } catch (e) { /* not seekable yet */ }
    }

    /* Progress across the sticky pin: 0 when the stage locks, 1 when it releases.
       That is the same window `contain 0%`..`contain 100%` describes in CSS. */
    progress() {
      const rect = this.root.getBoundingClientRect();
      const pin = rect.height - window.innerHeight;
      if (pin <= 0) return 0;
      return clamp(-rect.top / pin, 0, 1);
    }

    paint(pIn) {
      if (!this.beats || reduced.matches || this.dead) return;
      const p = typeof pIn === 'number' ? pIn : this.progress();
      this.root.style.setProperty('--mb-cine-p', p.toFixed(4));

      if (this.codecs) {
        this.paintCodecs(p);
      } else if (this.videoEnabled) {
        this.scrub(this.ritual, 'ritual', p);
        this.scrub(this.latte, 'latte', p);
      } else {
        /* before the drivesBeats gate: where scroll-driven CSS exists the beats
           are its job, but the canvas is always ours */
        this.drawFrame(p);
      }

      if (!this.drivesBeats) return;

      this.beats.forEach((el, i) => {
        const spec = BEATS[i];
        if (!spec) return;
        const [from, to] = spec.range;
        const t = clamp((p - from) / (to - from), 0, 1);
        el.style.opacity = track(t, spec.opacity).toFixed(3);
        el.style.transform = `translate3d(0, calc(${HANG_VIEWPORT.matches ? '0px' : '-50%'} + ${track(t, spec.shift).toFixed(1)}px), 0) scale(${track(t, spec.scale).toFixed(4)})`;
      });

      if (this.videoEnabled && !this.codecs) {
        if (this.ritual) this.ritual.style.opacity = track(p, RITUAL_OPACITY).toFixed(3);
        if (this.latte) this.latte.style.opacity = track(p, LATTE_OPACITY).toFixed(3);
      }
      if (this.media) this.media.style.transform = `scale(${(1.06 - 0.06 * p).toFixed(4)})`;
    }

    teardownScrollDriver() {
      cancelAnimationFrame(this.rafId);
      this.running = false;
      if (this.onResize) {
        window.removeEventListener('resize', this.onResize);
        window.removeEventListener('orientationchange', this.onResize);
        this.onResize = null;
      }
      if (!this.onScroll) return;
      window.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onScroll);
      this.onScroll = null;
    }

    onVisibility(entry) {
      document.body.classList.toggle(
        'mb-cinematic-header-active',
        entry.isIntersecting && entry.intersectionRatio > 0.6
      );
      if (entry.isIntersecting) this.play();
      else this.pause();
    }

    /* "play" now means "the scroll owns the playhead". The clips are never allowed to
       run on their own clock: that is what made this read as a looping video. */
    play() {
      if (reduced.matches) return;
      if (this.videoEnabled) this.videos.forEach((video) => this.primeForScrub(video));
      this.paint();
    }

    pause() {
      this.videos.forEach((video) => video.pause());
    }

    onReduced() {
      if (reduced.matches) {
        this.pause();
        if (!supportsTimeline) {
          this.teardownScrollDriver();
          this.root.classList.remove('mb-cinematic--js');
          this.root.classList.add('mb-cinematic--static');
        }
      } else {
        if (!this.onScroll) {
          this.root.classList.remove('mb-cinematic--static');
          this.setupScrollDriver();
        }
        this.play();
      }
    }

    destroy() {
      this.dead = true;
      if (this.scrubbers) this.scrubbers.forEach((s) => s.destroy());
      document.body.classList.remove('mb-cinematic-header-active');
      this.pause();
      this.teardownScrollDriver();
      this.observer.disconnect();
      reduced.removeEventListener('change', this.onReduced);
    }
  }

  const mount = (scope = document) => {
    scope.querySelectorAll(SELECTOR).forEach((root) => {
      if (!instances.has(root)) instances.set(root, new CinematicHero(root));
    });
  };

  const unmount = (scope) => {
    scope.querySelectorAll(SELECTOR).forEach((root) => {
      instances.get(root)?.destroy();
      instances.delete(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount());
  else mount();

  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => unmount(event.target));
})();
